import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Send,
  Save,
  Zap,
  Paperclip,
  Image,
  Link,
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Quote,
  Code,
  Smile,
  Calendar,
  Clock,
  User,
  Sparkles,
  Wand2,
  FileText,
  RefreshCw,
  X,
  Mail,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import EmailDraftModal from '@/components/EmailDraftModal';

const emailTemplates = [
  { id: 'professional', name: 'Professional', tone: 'formal' },
  { id: 'friendly', name: 'Friendly', tone: 'casual' },
  { id: 'sales', name: 'Sales Pitch', tone: 'persuasive' },
  { id: 'followup', name: 'Follow-up', tone: 'polite' },
  { id: 'thank-you', name: 'Thank You', tone: 'appreciative' },
  { id: 'apology', name: 'Apology', tone: 'apologetic' },
];

const aiSuggestions = [
  'Make it more concise',
  'Add a call-to-action',
  'Include specific details',
  'Make it more formal',
  'Add personal touch',
  'Improve subject line',
];

export default function EmailComposer() {
  const { toast } = useToast();
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  const generateWithAI = async () => {
    if (!aiPrompt) {
      toast({
        title: 'Please enter a prompt',
        description: 'Tell the AI what kind of email you want to write',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    
    // Simulate AI generation
    setTimeout(() => {
      const generatedSubject = `Meeting Follow-up: ${aiPrompt}`;
      const generatedBody = `Dear [Recipient],

I hope this email finds you well. ${aiPrompt}

I wanted to follow up on our recent discussion and share some key points:

1. Project timeline and milestones
2. Resource allocation and team responsibilities
3. Next steps and action items

I believe this approach will help us achieve our goals efficiently while maintaining high quality standards.

Please let me know if you have any questions or would like to discuss this further. I'm available for a call this week at your convenience.

Looking forward to your thoughts.

Best regards,
[Your Name]`;

      setSubject(generatedSubject);
      setBody(generatedBody);
      setIsGenerating(false);
      
      toast({
        title: 'Email generated!',
        description: 'AI has created a draft based on your prompt',
      });
    }, 2000);
  };

  const improveWithAI = (suggestion: string) => {
    setIsGenerating(true);
    
    setTimeout(() => {
      // Simulate AI improvement
      if (suggestion === 'Make it more concise') {
        setBody(body.split('\n').slice(0, -2).join('\n'));
      } else if (suggestion === 'Add a call-to-action') {
        setBody(body + '\n\nWould you be available for a 30-minute call this week to discuss next steps?');
      }
      
      setIsGenerating(false);
      toast({
        title: 'Email improved!',
        description: `Applied: ${suggestion}`,
      });
    }, 1000);
  };

  const sendEmail = () => {
    if (!to || !subject || !body) {
      toast({
        title: 'Missing required fields',
        description: 'Please fill in recipient, subject, and message',
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: 'Email sent!',
      description: 'Your email has been sent successfully',
    });
    
    // Reset form
    setTo('');
    setCc('');
    setBcc('');
    setSubject('');
    setBody('');
    setAttachments([]);
  };

  const saveDraft = () => {
    toast({
      title: 'Draft saved',
      description: 'Your email has been saved as a draft',
    });
  };

  const handleTemplateSelected = (content: { subject: string; htmlBody: string; textBody: string }) => {
    setSubject(content.subject);
    setBody(content.textBody);
    setShowTemplateModal(false);
    toast({
      title: 'Template loaded!',
      description: 'You can now customize and send the email.',
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Compose Email</h1>
          <p className="text-muted-foreground mt-1">
            Create and send emails with AI assistance
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowTemplateModal(true)}
          >
            <Mail className="mr-2 h-4 w-4" />
            Templates
          </Button>
          <EmailDraftModal
            isOpen={showTemplateModal}
            onClose={() => setShowTemplateModal(false)}
            onSelectTemplate={handleTemplateSelected}
          />
          <Button variant="outline" onClick={saveDraft}>
            <Save className="mr-2 h-4 w-4" />
            Save Draft
          </Button>
          <Button onClick={sendEmail} className="bg-primary hover:bg-primary/90">
            <Send className="mr-2 h-4 w-4" />
            Send Email
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Composer */}
        <div className="lg:col-span-2 space-y-4">
          {/* Recipients */}
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor="to">To</Label>
                <Input
                  id="to"
                  placeholder="recipient@example.com"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cc">Cc</Label>
                  <Input
                    id="cc"
                    placeholder="cc@example.com"
                    value={cc}
                    onChange={(e) => setCc(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="bcc">Bcc</Label>
                  <Input
                    id="bcc"
                    placeholder="bcc@example.com"
                    value={bcc}
                    onChange={(e) => setBcc(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subject */}
          <Card>
            <CardContent className="pt-6">
              <Label htmlFor="subject">Subject</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="subject"
                  placeholder="Email subject..."
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="flex-1"
                />
                <Button variant="outline" size="icon">
                  <Wand2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Email Body */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Message</CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Underline className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <List className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Quote className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Code className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Link className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Image className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Smile className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Write your email here..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[300px] font-sans"
              />
            </CardContent>
          </Card>

          {/* Attachments */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-3">
                <Label>Attachments</Label>
                <Button variant="outline" size="sm">
                  <Paperclip className="mr-2 h-3 w-3" />
                  Add File
                </Button>
              </div>
              {attachments.length > 0 ? (
                <div className="space-y-2">
                  {attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded-lg border"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{file}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => setAttachments(attachments.filter((_, i) => i !== index))}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No attachments</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Assistant Sidebar */}
        <div className="space-y-4">
          {/* AI Generator */}
          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                AI Email Generator
              </CardTitle>
              <CardDescription>
                Let AI help you write the perfect email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="ai-prompt">What do you want to write about?</Label>
                <Textarea
                  id="ai-prompt"
                  placeholder="E.g., Follow up on our meeting about the Q4 marketing strategy..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  className="mt-1 min-h-[100px]"
                />
              </div>
              <Button
                onClick={generateWithAI}
                disabled={isGenerating}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate Email
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Email Templates</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template" />
                </SelectTrigger>
                <SelectContent>
                  {emailTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{template.name}</span>
                        <Badge variant="secondary" className="ml-2 text-xs">
                          {template.tone}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* AI Suggestions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">AI Suggestions</CardTitle>
              <CardDescription>Click to apply improvements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {aiSuggestions.map((suggestion, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => improveWithAI(suggestion)}
                    disabled={isGenerating || !body}
                  >
                    <Wand2 className="mr-2 h-3 w-3" />
                    {suggestion}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Schedule Send */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Schedule Send</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="schedule-date">Date & Time</Label>
                <Input
                  id="schedule-date"
                  type="datetime-local"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
              <Button variant="outline" className="w-full">
                <Clock className="mr-2 h-4 w-4" />
                Schedule Email
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}